export const PARTICIPANT_GRID_MAX_VISIBLE_TILES = 6;
export const PARTICIPANT_GRID_MIN_TILE_HEIGHT_PX = 200;
export const PARTICIPANT_GRID_MOBILE_MIN_TILE_HEIGHT_PX = 120;
export const PARTICIPANT_GRID_MOBILE_TWO_BY_TWO_MIN_TILE_WIDTH_PX = 160;
export const PARTICIPANT_GRID_GAP_PX = 8;
export const PARTICIPANT_GRID_VERTICAL_PADDING_PX = 140;
export const PARTICIPANT_GRID_SHORT_VERTICAL_PADDING_PX = 24;
export const PARTICIPANT_GRID_TINY_VERTICAL_PADDING_PX = 0;
export const PARTICIPANT_GRID_HORIZONTAL_PADDING_PX = 24;
export const PARTICIPANT_GRID_TILE_ASPECT_RATIO = 16 / 9;
const PARTICIPANT_GRID_TINY_VERTICAL_PADDING_MAX_HEIGHT = 260;

const WIDTH_BREAKPOINTS = [
  { name: "xxl", min: 1600 },
  { name: "xll", min: 1440 },
  { name: "xl", min: 1200 },
  { name: "lg", min: 992 },
  { name: "md", min: 768 },
  { name: "sm", min: 576 },
  { name: "xs", min: 0 }
];
const HEIGHT_BREAKPOINTS = [
  { name: "tall", min: 1072 },
  { name: "comfortable", min: 896 },
  { name: "regular", min: 756 },
  { name: "compact", min: 460 },
  { name: "short", min: 0 }
];

function normalizeViewportDimension(value) {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function getBreakpointName(breakpoints, value) {
  const normalizedValue = normalizeViewportDimension(value);

  return (
    breakpoints.find(breakpoint => normalizedValue >= breakpoint.min)?.name || breakpoints[breakpoints.length - 1].name
  );
}

function isMobileWidthBreakpoint(widthBreakpoint) {
  return widthBreakpoint === "xs" || widthBreakpoint === "sm";
}

function isCompactWidthBreakpoint(widthBreakpoint) {
  return widthBreakpoint === "xs" || widthBreakpoint === "sm" || widthBreakpoint === "md";
}

function getParticipantGridMinimumVisibleTileCount(participantCount, widthBreakpoint) {
  if (participantCount <= 0) {
    return 0;
  }

  if (isMobileWidthBreakpoint(widthBreakpoint)) {
    return 1;
  }

  if (isCompactWidthBreakpoint(widthBreakpoint)) {
    return Math.min(participantCount, 2);
  }

  return Math.min(participantCount, 3);
}

function getParticipantGridVerticalPadding(viewportHeight) {
  const heightBreakpoint = getBreakpointName(HEIGHT_BREAKPOINTS, viewportHeight);

  if (viewportHeight <= PARTICIPANT_GRID_TINY_VERTICAL_PADDING_MAX_HEIGHT) {
    return PARTICIPANT_GRID_TINY_VERTICAL_PADDING_PX;
  }

  return heightBreakpoint === "short"
    ? PARTICIPANT_GRID_SHORT_VERTICAL_PADDING_PX
    : PARTICIPANT_GRID_VERTICAL_PADDING_PX;
}

function getParticipantGridShape(visibleTileCount, widthBreakpoint) {
  if (visibleTileCount <= 0) {
    return {
      columnCount: 0,
      rowUnitCount: 0,
      rowGapCount: 0
    };
  }

  if (isMobileWidthBreakpoint(widthBreakpoint)) {
    return {
      columnCount: 1,
      rowUnitCount: visibleTileCount,
      rowGapCount: Math.max(0, visibleTileCount - 1)
    };
  }

  if (visibleTileCount === 5) {
    return isCompactWidthBreakpoint(widthBreakpoint)
      ? {
          columnCount: 2,
          rowUnitCount: 4,
          rowGapCount: 2
        }
      : {
          columnCount: 3,
          rowUnitCount: 2.5,
          rowGapCount: 1
        };
  }

  if (isCompactWidthBreakpoint(widthBreakpoint) && visibleTileCount >= 6) {
    return {
      columnCount: 2,
      rowUnitCount: Math.ceil(visibleTileCount / 2),
      rowGapCount: Math.max(0, Math.ceil(visibleTileCount / 2) - 1)
    };
  }

  if (visibleTileCount === 4) {
    return {
      columnCount: 2,
      rowUnitCount: 2,
      rowGapCount: 1
    };
  }

  const columnCount = Math.min(3, visibleTileCount);
  const rowCount = Math.ceil(visibleTileCount / columnCount);

  return {
    columnCount,
    rowUnitCount: rowCount,
    rowGapCount: Math.max(0, rowCount - 1)
  };
}

function canFitParticipantGridVisibleTileCount({
  visibleTileCount,
  widthBreakpoint,
  viewportWidth,
  viewportHeight,
  minTileHeight = PARTICIPANT_GRID_MIN_TILE_HEIGHT_PX
}) {
  if (visibleTileCount <= 0) {
    return true;
  }

  const { columnCount, rowUnitCount, rowGapCount } = getParticipantGridShape(visibleTileCount, widthBreakpoint);
  const availableWidth = Math.max(0, viewportWidth - PARTICIPANT_GRID_HORIZONTAL_PADDING_PX);
  const availableHeight = Math.max(0, viewportHeight - getParticipantGridVerticalPadding(viewportHeight));
  const columnGapCount = Math.max(0, columnCount - 1);
  const maxTileHeightByWidth =
    (availableWidth - PARTICIPANT_GRID_GAP_PX * columnGapCount) / columnCount / PARTICIPANT_GRID_TILE_ASPECT_RATIO;
  const maxTileHeightByHeight = (availableHeight - PARTICIPANT_GRID_GAP_PX * rowGapCount) / Math.max(1, rowUnitCount);

  if (isMobileWidthBreakpoint(widthBreakpoint)) {
    return (
      maxTileHeightByWidth >= PARTICIPANT_GRID_MOBILE_MIN_TILE_HEIGHT_PX &&
      maxTileHeightByHeight >= maxTileHeightByWidth
    );
  }

  return Math.min(maxTileHeightByWidth, maxTileHeightByHeight) >= minTileHeight;
}

function canFitMobileTwoByTwoGrid({ viewportWidth, viewportHeight }) {
  const availableWidth = Math.max(0, viewportWidth - PARTICIPANT_GRID_HORIZONTAL_PADDING_PX);
  const availableHeight = Math.max(0, viewportHeight - getParticipantGridVerticalPadding(viewportHeight));
  const tileWidth = (availableWidth - PARTICIPANT_GRID_GAP_PX) / 2;
  const tileHeight = (availableHeight - PARTICIPANT_GRID_GAP_PX) / 2;

  return (
    tileWidth >= PARTICIPANT_GRID_MOBILE_TWO_BY_TWO_MIN_TILE_WIDTH_PX &&
    tileHeight >= PARTICIPANT_GRID_MOBILE_MIN_TILE_HEIGHT_PX
  );
}

export function getParticipantGridViewportBreakpoints({ width = 0, height = 0 } = {}) {
  return {
    width: getBreakpointName(WIDTH_BREAKPOINTS, width),
    height: getBreakpointName(HEIGHT_BREAKPOINTS, height)
  };
}

export function getParticipantGridMaxVisibleTileCount({
  participantCount = 0,
  viewportWidth = 0,
  viewportHeight = 0,
  minTileHeight = PARTICIPANT_GRID_MIN_TILE_HEIGHT_PX
} = {}) {
  const safeParticipantCount = Math.floor(Math.max(0, Number.isFinite(participantCount) ? participantCount : 0));
  const maxCandidate = Math.min(safeParticipantCount, PARTICIPANT_GRID_MAX_VISIBLE_TILES);

  if (maxCandidate <= 0) {
    return 0;
  }

  if (safeParticipantCount <= 2) {
    return safeParticipantCount;
  }

  const { width } = getParticipantGridViewportBreakpoints({
    width: viewportWidth,
    height: viewportHeight
  });
  const minimumCandidate = getParticipantGridMinimumVisibleTileCount(safeParticipantCount, width);
  const canFitMobileTwoByTwo =
    isMobileWidthBreakpoint(width) && canFitMobileTwoByTwoGrid({ viewportWidth, viewportHeight });

  if (safeParticipantCount >= 4 && canFitMobileTwoByTwo) {
    return 4;
  }

  for (let visibleTileCount = maxCandidate; visibleTileCount >= minimumCandidate; visibleTileCount--) {
    const canFitVisibleTileCount = canFitParticipantGridVisibleTileCount({
      visibleTileCount,
      widthBreakpoint: width,
      viewportWidth,
      viewportHeight,
      minTileHeight
    });

    if (canFitVisibleTileCount) {
      return visibleTileCount;
    }
  }

  return minimumCandidate;
}
